import type {
  DynamicModalAutocompleteOption,
  DynamicModalFieldConfig,
} from '@e-learning/shared';
import type { UserGroupDetail } from '@e-learning/core';
import {
  DynamicModal,
  DynamicModalFormValues,
  Groups,
  Toast,
  iconCheckWhiteModalGroups,
  useTranslation,
  type GroupsItem,
} from '@e-learning/shared';
import { useCallback, useEffect, useState } from 'react';
import { useGroups as useGroupsHook } from './hooks';

const USERS_PAGE_SIZE = 3;

function parseUserIds(value: string | undefined): string[] {
  return (value ?? '')
    .split(',')
    .map(s => s.trim())
    .filter(Boolean);
}

export default function GroupsPage() {
  const { t } = useTranslation();
  const {
    fetchGroups,
    fetchUsersForGroupSelection,
    createGroup,
    getGroup,
    updateGroup,
    addUsersToGroup,
    deleteGroup,
  } = useGroupsHook();
  const [modalOpen, setModalOpen] = useState(false);
  const [editingGroupId, setEditingGroupId] = useState<string | null>(null);
  const [editingGroupDetail, setEditingGroupDetail] =
    useState<UserGroupDetail | null>(null);
  const [modalInitialValues, setModalInitialValues] = useState<
    Partial<DynamicModalFormValues>
  >({});
  const [groupToDelete, setGroupToDelete] = useState<GroupsItem | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [toastMessage, setToastMessage] = useState<{
    text: string;
    variant: 'success' | 'error';
  } | null>(null);
  const [userOptions, setUserOptions] = useState<
    DynamicModalAutocompleteOption[]
  >([]);

  // refreshTrigger na dependência força Groups a refazer o fetch quando a lista é atualizada (ex.: após criar grupo).
  const fetchGroupsWithRefresh = useCallback(
    async (params: { page: number; limit: number; search?: string }) => {
      return fetchGroups(params);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refreshTrigger intencional para refetch.
    [fetchGroups, refreshTrigger]
  );

  useEffect(() => {
    if (!toastMessage) return;
    const id = setTimeout(() => setToastMessage(null), 4000);
    return () => clearTimeout(id);
  }, [toastMessage]);

  const loadUserOptions = useCallback(
    async ({
      search,
      page,
      pageSize,
    }: {
      search: string;
      page: number;
      pageSize: number;
    }) => {
      try {
        const { data, total } = await fetchUsersForGroupSelection({
          page,
          search: search || undefined,
        });
        const options: DynamicModalAutocompleteOption[] = data.map(
          (u: { id: string; name: string; email: string }) => ({
            value: u.id,
            label: u.name || u.email,
          })
        );

        setUserOptions(prev => {
          const map = new Map<string, DynamicModalAutocompleteOption>(
            prev.map(o => [o.value, o] as const)
          );
          options.forEach(o => map.set(o.value, o));
          return Array.from(map.values());
        });

        const size = pageSize ?? USERS_PAGE_SIZE;
        return {
          options,
          total,
          hasMore: page * size < total,
        };
      } catch {
        if (page === 1) {
          setUserOptions([]);
        }
        return {
          options: [],
          total: 0,
          hasMore: false,
        };
      }
    },
    [fetchUsersForGroupSelection]
  );

  const handleCreateGroup = useCallback(() => {
    setEditingGroupId(null);
    setEditingGroupDetail(null);
    setModalInitialValues({});
    setModalOpen(true);
  }, []);

  const handleEdit = useCallback(
    async (item: GroupsItem) => {
      setEditingGroupId(item.id);
      try {
        const detail = await getGroup(item.id);
        setEditingGroupDetail(detail);
        const userIds = Array.isArray(detail.users)
          ? detail.users.map(u => u.userId).join(',')
          : '';
        setModalInitialValues({
          groupName: detail.name,
          description: detail.description ?? '',
          users: userIds,
        });
        if (Array.isArray(detail.users) && detail.users.length > 0) {
          const editOptions: DynamicModalAutocompleteOption[] = detail.users.map(
            u => ({ value: u.userId, label: u.name || u.userId })
          );
          setUserOptions(prev => {
            const map = new Map<string, DynamicModalAutocompleteOption>(
              prev.map(o => [o.value, o] as const)
            );
            editOptions.forEach(o => map.set(o.value, o));
            return Array.from(map.values());
          });
        }
        setModalOpen(true);
      } catch {
        setToastMessage({
          text: t('groups.update_error'),
          variant: 'error',
        });
        setEditingGroupId(null);
        setEditingGroupDetail(null);
      }
    },
    [getGroup, t]
  );

  const handleModalSubmit = useCallback(
    async (values: DynamicModalFormValues) => {
      if (isSubmitting) return;
      const name = values.groupName?.trim();
      const description = (values.description ?? '').trim();
      if (!name) {
        setToastMessage({
          text: editingGroupId
            ? t('groups.update_error')
            : t('groups.create_error'),
          variant: 'error',
        });
        return;
      }
      setIsSubmitting(true);
      try {
        if (editingGroupId) {
          await updateGroup(editingGroupId, {
            name,
            description,
            isActive: editingGroupDetail?.isActive ?? true,
          });
          const userIds = parseUserIds(values.users);
          if (userIds.length > 0) {
            await addUsersToGroup(editingGroupId, { userIds });
          }
          setModalOpen(false);
          setEditingGroupId(null);
          setEditingGroupDetail(null);
          setModalInitialValues({});
          setRefreshTrigger(prev => prev + 1);
          setToastMessage({
            text: t('groups.update_success'),
            variant: 'success',
          });
        } else {
          const userIds = parseUserIds(values.users);
          const group = await createGroup({ name, description });
          if (userIds.length > 0) {
            await addUsersToGroup(group.id, { userIds });
          }
          setModalOpen(false);
          setRefreshTrigger(prev => prev + 1);
          setToastMessage({
            text: t('groups.create_success'),
            variant: 'success',
          });
        }
      } catch {
        setToastMessage({
          text: editingGroupId
            ? t('groups.update_error')
            : t('groups.create_error'),
          variant: 'error',
        });
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      isSubmitting,
      editingGroupId,
      editingGroupDetail,
      updateGroup,
      createGroup,
      addUsersToGroup,
      t,
    ]
  );

  const handleCloseCreateEditModal = useCallback(() => {
    if (!isSubmitting) {
      setModalOpen(false);
      setEditingGroupId(null);
      setEditingGroupDetail(null);
      setModalInitialValues({});
    }
  }, [isSubmitting]);

  const handleDelete = useCallback((item: GroupsItem) => {
    setGroupToDelete(item);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!groupToDelete || isDeleting) return;
    setIsDeleting(true);
    try {
      await deleteGroup(groupToDelete.id);
      setGroupToDelete(null);
      setRefreshTrigger(prev => prev + 1);
      setToastMessage({
        text: t('groups.delete_success'),
        variant: 'success',
      });
    } catch {
      setToastMessage({
        text: t('groups.delete_error'),
        variant: 'error',
      });
    } finally {
      setIsDeleting(false);
    }
  }, [groupToDelete, isDeleting, deleteGroup, t]);

  const modalFields: DynamicModalFieldConfig[] = [
    {
      name: 'groupName',
      label: t('groups.modal_field_name_label'),
      type: 'text',
      placeholder: t('groups.modal_field_name_placeholder'),
      optional: false,
    },
    {
      name: 'description',
      label: t('groups.modal_field_description_label'),
      type: 'textarea',
      placeholder: t('groups.modal_field_description_placeholder'),
      optional: true,
    },
    {
      name: 'users',
      label: t('groups.modal_field_users_label'),
      type: 'autocomplete',
      placeholder: t('groups.modal_field_users_placeholder'),
      optional: false,
      multiselect: true,
      options: userOptions,
      loadOptions: loadUserOptions,
      pageSize: USERS_PAGE_SIZE,
    },
  ];

  return (
    <div data-e2e="page-admin-groups">
      <Groups
        fetchGroups={fetchGroupsWithRefresh}
        onCreateGroup={handleCreateGroup}
        onEdit={handleEdit}
        onDelete={handleDelete}
      />
      <DynamicModal
        title={
          editingGroupId
            ? t('groups.modal_edit_title')
            : t('groups.modal_create_title')
        }
        open={modalOpen}
        onClose={handleCloseCreateEditModal}
        closeOnOverlayClick={false}
        initialValues={modalInitialValues}
        fields={modalFields}
        actions={[
          {
            label: t('groups.modal_action_cancel'),
            onClick: handleCloseCreateEditModal,
            variant: 'secondary',
            outlined: true,
          },
          {
            label: isSubmitting
              ? editingGroupId
                ? t('groups.modal_action_saving')
                : t('groups.modal_action_creating')
              : editingGroupId
                ? t('groups.modal_action_save')
                : t('groups.modal_action_create'),
            onClick: handleModalSubmit,
            variant: 'primary',
            iconSrc: iconCheckWhiteModalGroups,
            iconSize: 18,
            iconAlt: isSubmitting
              ? editingGroupId
                ? t('groups.modal_action_saving')
                : t('groups.modal_action_creating')
              : editingGroupId
                ? t('groups.modal_action_save')
                : t('groups.modal_action_create'),
          },
        ]}
        dataE2ePrefix="groups-modal"
      />
      <DynamicModal
        title={
          groupToDelete
            ? t('groups.delete_confirm_title', { name: groupToDelete.name })
            : ''
        }
        description={t('groups.delete_confirm_message')}
        compact
        open={!!groupToDelete}
        onClose={() => !isDeleting && setGroupToDelete(null)}
        closeOnOverlayClick={false}
        fields={[]}
        actions={[
          {
            label: t('groups.modal_action_cancel'),
            onClick: () => setGroupToDelete(null),
            variant: 'secondary',
          },
          {
            label: isDeleting
              ? t('groups.modal_action_deleting')
              : t('groups.modal_action_delete'),
            onClick: handleConfirmDelete,
            variant: 'primary',
          },
        ]}
        dataE2ePrefix="groups-delete-confirm"
      />
      {toastMessage && (
        <Toast
          message={toastMessage.text}
          variant={toastMessage.variant}
          role="alert"
        />
      )}
    </div>
  );
}
